import { useEffect, useState } from 'react';
import { getMediaUrl, parseStorageKey } from '@vibesites/ui-primitives';
import { contrastRatio, parseColor } from './luminance';

/** WCAG 1.4.11 non-text contrast minimum for graphical objects. */
const LOGO_MIN_CONTRAST = 3;
/** Share of the logo's visible pixels that must clear LOGO_MIN_CONTRAST. */
export const LOGO_MIN_LEGIBLE_SHARE = 0.5;
/** Above this opaque share the image carries its own backdrop (e.g. a JPG). */
const OPAQUE_BACKDROP_SHARE = 0.97;
const SAMPLE_WIDTH = 96;

const cache = new Map<string, number | null>();

function samplingUrl(url: string): string {
  const key = parseStorageKey(url);
  return key ? getMediaUrl(key, { width: SAMPLE_WIDTH * 2 }) : url;
}

function measure(img: HTMLImageElement, background: string): number | null {
  const bg = parseColor(background);
  if (!bg || !img.naturalWidth || !img.naturalHeight) return null;
  const width = Math.min(SAMPLE_WIDTH, img.naturalWidth);
  const height = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * width));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, width, height);

  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, width, height).data;
  } catch {
    return null;
  }

  let opaque = 0;
  let legible = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue;
    opaque++;
    if (contrastRatio({ r: data[i], g: data[i + 1], b: data[i + 2] }, bg) >= LOGO_MIN_CONTRAST) {
      legible++;
    }
  }
  const total = width * height;
  if (!opaque) return null;
  if (opaque / total >= OPAQUE_BACKDROP_SHARE) return 1;
  return legible / opaque;
}

/**
 * Samples a logo image's visible pixels and returns the share (0–1) that
 * clear the non-text contrast minimum against the given background color.
 * Returns `null` while loading, during SSR, or when the image can't be read
 * (CORS-tainted canvas) so callers can fall back.
 */
export function useLogoLegibility(
  url: string | null | undefined,
  background: string | null | undefined,
): number | null {
  const cacheKey = url && background ? `${url}|${background}` : '';
  const [share, setShare] = useState<number | null>(() => (cacheKey ? cache.get(cacheKey) ?? null : null));

  useEffect(() => {
    if (!cacheKey || !url || !background) {
      setShare(null);
      return;
    }
    if (cache.has(cacheKey)) {
      setShare(cache.get(cacheKey) ?? null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      const result = measure(img, background);
      cache.set(cacheKey, result);
      if (!cancelled) setShare(result);
    };
    img.onerror = () => {
      cache.set(cacheKey, null);
      if (!cancelled) setShare(null);
    };
    img.src = samplingUrl(url);
    return () => {
      cancelled = true;
    };
  }, [cacheKey, url, background]);

  return share;
}
